import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { compose, withHandlers, withState } from 'recompose';

import S from 'app/features/authentication/components/Field.styled';
import noop from 'app/libs/noop';
import Text from 'app/components/layout/Text.styled';

const Field = ({
  name,
  value,
  onChange,
  isFocused,
  shouldValidate,
  floatingTextKey,
  errorTextKey,
  passwordWithSpaceTextKey,
  isValid,
  containsSpace,
  onBlur,
  onKeyPress,
  onFocus,
  type,
  testId,
  as,
  children,
}) => {
  const isEmpty = value.length === 0;
  const isDirty = Boolean(value.length);
  const isInitial = isEmpty && !isFocused;
  const hasSpaces =
    !isEmpty && passwordWithSpaceTextKey && containsSpace(value);
  const isError =
    (errorTextKey && !isEmpty && shouldValidate && !isValid(value)) ||
    hasSpaces;
  const isSuccess = !isInitial && !isError;
  const errorText = hasSpaces ? passwordWithSpaceTextKey : errorTextKey;

  return (
    <S.Field
      isError={isError}
      isSuccess={isSuccess}
      as={as}
      isFocused={isFocused}
      isDirty={isDirty}
    >
      <Text isFixedSize size="xl" as={S.Field.Description}>
        <FormattedMessage id={isError ? errorText : floatingTextKey} />
      </Text>
      <Text
        size="xl"
        isFixedSize
        as={S.Field.Input}
        name={name}
        type={type}
        value={value}
        maxLength={255}
        autoComplete="on"
        onChange={onChange}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        onFocus={onFocus}
        isFocused={isFocused}
        data-t={testId}
      />
      {typeof children === 'function' ? children(isFocused) : children}
    </S.Field>
  );
};

Field.propTypes = {
  floatingTextKey: PropTypes.string.isRequired,
  errorTextKey: PropTypes.string.isRequired,
  passwordWithSpaceTextKey: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  type: PropTypes.string,
  isValid: PropTypes.func,
  containsSpace: PropTypes.func,
  isFocused: PropTypes.bool,
  shouldValidate: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  onFocus: PropTypes.func,
  testId: PropTypes.string,
  as: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.string,
    PropTypes.shape({ render: PropTypes.func.isRequired }),
  ]),
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
};

Field.defaultProps = {
  value: '',
  type: 'text',
  isFocused: false,
  isValid: () => true,
  containsSpace: () => false,
  passwordWithSpaceTextKey: '',
  shouldValidate: false,
  onBlur: noop,
  onChange: noop,
  onKeyPress: noop,
  onFocus: noop,
  testId: '',
  as: null,
  children: null,
};

export { Field };

export default compose(
  withState('value', 'setValue', ''),
  withState('isFocused', 'setFocus', false),
  withState('shouldValidate', 'setShouldValidate', false),
  withHandlers({
    onChange:
      ({ setValue, onValueChange }) =>
      event => {
        setValue(event.target.value);
        onValueChange(event.target.value);
      },
    onFocus:
      ({ setFocus }) =>
      () =>
        setFocus(true),
    onBlur:
      ({ setFocus, setShouldValidate, value, shouldValidate }) =>
      () => {
        setFocus(false);
        if (!shouldValidate && value.trim()) {
          setShouldValidate(true);
        }
      },
  }),
)(Field);
